import axios from 'axios';

// Base URL for the API - adjust this to match your backend
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
// export const API_BASE_URL =  'http://localhost:8000';

/**
 * Fetch all TVs from the backend
 * @returns {Promise<Array>} Array of TV objects
 */
export const fetchAllTVs = async (projectId) => {
  try {
    // Multi-city: scope the device list to the active city. The backend also
    // restricts a scoped user to their own cities when this is omitted.
    const response = await axios.get(`${API_BASE_URL}/api/cms-devices`, {
      params: projectId ? { projectId } : {},
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching TVs:', error);
    throw error;
  }
};

/**
 * Fetch a specific TV by ID
 * @param {string} tvId - The TV ID to fetch
 * @returns {Promise<Object>} TV object
 */
export const fetchTVById = async (tvId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/cms-devices/${tvId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching TV ${tvId}:`, error);
    throw error;
  }
};

/**
 * Update device metadata (displayName, tvId, location, status).
 * Used by the "rename screen" flow — pass an empty displayName to clear it.
 *
 * @param {string} deviceId - Hardware deviceId (the primary key on cms_device)
 * @param {{ displayName?: string, tvId?: string, location?: string, status?: string }} patch
 * @returns {Promise<Object>} Updated device
 */
export const updateDevice = async (deviceId, patch) => {
  if (!deviceId) throw new Error('deviceId is required');
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/api/cms-devices/${encodeURIComponent(deviceId)}`,
      patch || {}
    );
    return response.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Update failed';
    console.error(`Error updating device ${deviceId}:`, msg);
    throw new Error(msg);
  }
};

/**
 * DELETE /api/cms-devices/:deviceId — hard-deletes the device and cascades
 * schedule slots, screen_schedule, tvAssignment; cancels schedule_intents.
 * Audit trails (screen_events, tvHistory) are preserved. Returns
 * { success, deleted, device_id, cascaded: {...} }.
 */
export const deleteScreen = async (deviceId) => {
  if (!deviceId) throw new Error('deviceId is required');
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/cms-devices/${encodeURIComponent(deviceId)}`
    );
    return response.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Delete failed';
    console.error(`Error deleting device ${deviceId}:`, msg);
    throw new Error(msg);
  }
};

// ──────────────────────────────────────────────────────────────────────────
// Screen Profile — per-device profile apply / confirm / clear / bulk.
// The profile CRUD itself lives in ./profileApi.js; these wrappers are
// device-scoped because they live on /api/cms-devices/... and mutate the
// device's profile_id. See backend cms.device.controller.js for semantics.
// ──────────────────────────────────────────────────────────────────────────

/**
 * Apply a profile to a device. Clears any pending hardware-change snapshot
 * on the device because applying a profile is inherently a "yes, current
 * state is correct" acknowledgement.
 */
export const applyDeviceProfile = async (deviceId, profileId, { confirmedBy } = {}) => {
  if (!deviceId) throw new Error('deviceId is required');
  if (!profileId) throw new Error('profileId is required');
  try {
    const res = await axios.post(
      `${API_BASE_URL}/api/cms-devices/${encodeURIComponent(deviceId)}/profile`,
      { profileId, confirmedBy }
    );
    return res.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Apply profile failed';
    console.error(`[applyDeviceProfile] ${deviceId}:`, msg);
    throw new Error(msg);
  }
};

/**
 * "Keep current profile" after a hardware-change alert. Same as apply,
 * except it doesn't take a profileId — it just clears the previous_edid
 * snapshot so the warning card disappears.
 */
export const confirmDeviceProfile = async (deviceId, { confirmedBy } = {}) => {
  if (!deviceId) throw new Error('deviceId is required');
  try {
    const res = await axios.post(
      `${API_BASE_URL}/api/cms-devices/${encodeURIComponent(deviceId)}/profile/confirm`,
      { confirmedBy }
    );
    return res.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Confirm profile failed';
    console.error(`[confirmDeviceProfile] ${deviceId}:`, msg);
    throw new Error(msg);
  }
};

/** Un-profile a device — fall back to native_resolution_*. Rare. */
export const clearDeviceProfile = async (deviceId) => {
  if (!deviceId) throw new Error('deviceId is required');
  try {
    const res = await axios.delete(
      `${API_BASE_URL}/api/cms-devices/${encodeURIComponent(deviceId)}/profile`
    );
    return res.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Clear profile failed';
    console.error(`[clearDeviceProfile] ${deviceId}:`, msg);
    throw new Error(msg);
  }
};

/**
 * Bulk apply a profile to multiple devices in one call. Used by the Screens
 * tab multi-select action when rolling out a profile to many kiosks.
 * Returns { matched, modified, profileId }.
 */
export const bulkApplyDeviceProfile = async (deviceIds, profileId, { confirmedBy } = {}) => {
  if (!Array.isArray(deviceIds) || deviceIds.length === 0) {
    throw new Error('deviceIds must be a non-empty array');
  }
  if (!profileId) throw new Error('profileId is required');
  try {
    const res = await axios.post(
      `${API_BASE_URL}/api/cms-devices/profile/bulk-apply`,
      { deviceIds, profileId, confirmedBy }
    );
    return res.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Bulk apply failed';
    console.error('[bulkApplyDeviceProfile]', msg);
    throw new Error(msg);
  }
};

// ──────────────────────────────────────────────────────────────────────────
// Calibration — per-screen overscan + color adjustment.
// ──────────────────────────────────────────────────────────────────────────

/** Save calibration values for a device. */
export const saveDeviceCalibration = async (deviceId, calibration) => {
  if (!deviceId) throw new Error('deviceId is required');
  try {
    const res = await axios.put(
      `${API_BASE_URL}/api/cms-devices/${encodeURIComponent(deviceId)}/calibration`,
      calibration
    );
    return res.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Save calibration failed';
    console.error(`[saveDeviceCalibration] ${deviceId}:`, msg);
    throw new Error(msg);
  }
};

/** Clear calibration (reset to defaults). */
export const clearDeviceCalibration = async (deviceId) => {
  if (!deviceId) throw new Error('deviceId is required');
  try {
    const res = await axios.delete(
      `${API_BASE_URL}/api/cms-devices/${encodeURIComponent(deviceId)}/calibration`
    );
    return res.data;
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message || 'Clear calibration failed';
    console.error(`[clearDeviceCalibration] ${deviceId}:`, msg);
    throw new Error(msg);
  }
};

/**
 * Create a new schedule
 * @param {Object} scheduleData - Schedule data object
 * @returns {Promise<Object>} Created schedule object
 */
export const createSchedule = async (scheduleData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/schedules`, scheduleData);
    return response.data;
  } catch (error) {
    console.error('Error creating schedule:', error);
    throw error;
  }
};

/**
 * Get all schedules, optionally filtered by TV ID
 * @param {string} tvId - Optional TV ID to filter by
 * @returns {Promise<Array>} Array of schedule objects
 */
export const getSchedules = async (tvId = null) => {
  try {
    const url = tvId 
      ? `${API_BASE_URL}/api/schedules?tvId=${tvId}`
      : `${API_BASE_URL}/api/schedules`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching schedules:', error);
    throw error;
  }
};

/**
 * Update an existing schedule
 * @param {string} scheduleId - The schedule ID to update
 * @param {Object} scheduleData - Updated schedule data
 * @returns {Promise<Object>} Updated schedule object
 */
export const updateSchedule = async (scheduleId, scheduleData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/schedules/${scheduleId}`, scheduleData);
    return response.data;
  } catch (error) {
    console.error(`Error updating schedule ${scheduleId}:`, error);
    throw error;
  }
};

/**
 * Delete a schedule
 * @param {string} scheduleId - The schedule ID to delete
 * @returns {Promise<void>}
 */
export const deleteSchedule = async (scheduleId) => {
  try {
    await axios.delete(`${API_BASE_URL}/api/schedules/${scheduleId}`);
  } catch (error) {
    console.error(`Error deleting schedule ${scheduleId}:`, error);
    throw error;
  }
};

